import React from "react";
import { Divider, FormControlLabel, Grid } from "@material-ui/core";
import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { Controller, useWatch, Control, FieldErrors } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { SectionProps } from "./SectionProps";
import { TextField } from "@ofqual/portal-components/components/TextField";

export const AffectedLearners: React.FC<SectionProps<EventNotification>> = ({
  control,
  errors,
  disabled,
}) => {
  const {
    englandCentresAffected,
    niCentresAffected,
    walesCentresAffected,
    otherCentresAffected,
  } = useWatch({
    control,
    name: [
      "englandCentresAffected",
      "niCentresAffected",
      "walesCentresAffected",
      "otherCentresAffected",
      "englandCentresEntered",
    ],
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="englandCentresAffected"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e: any) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  disabled={disabled}
                  data-test="england"
                />
              }
              label="England"
            />
          )}
        />
      </Grid>
      {englandCentresAffected && (
        <>
          {renderNumberFields(control, "england", "Centres", errors, disabled)}
          {renderNumberFields(control, "england", "Learners", errors, disabled)}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="niCentresAffected"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e: any) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  disabled={disabled}
                  data-test="ni"
                />
              }
              label="Northern Ireland"
            />
          )}
        />
      </Grid>
      {niCentresAffected && (
        <>
          {renderNumberFields(control, "ni", "Centres", errors, disabled)}
          {renderNumberFields(control, "ni", "Learners", errors, disabled)}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="walesCentresAffected"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e: any) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  disabled={disabled}
                  data-test="wales"
                />
              }
              label="Wales"
            />
          )}
        />
      </Grid>
      {walesCentresAffected && (
        <>
          {renderNumberFields(control, "wales", "Centres", errors, disabled)}
          {renderNumberFields(control, "wales", "Learners", errors, disabled)}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Controller
          control={control}
          name="otherCentresAffected"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e: any) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  disabled={disabled}
                  data-test="other"
                />
              }
              label="Other"
            />
          )}
        />
      </Grid>
      {otherCentresAffected && (
        <>
          {renderNumberFields(control, "other", "Centres", errors, disabled)}
          {renderNumberFields(control, "other", "Learners", errors, disabled)}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </>
      )}
    </Grid>
  );
};

const renderNumberFields = (
  control: Control,
  nation: "england" | "wales" | "ni" | "other",
  type: "Centres" | "Learners",
  errors: FieldErrors<EventNotification>,
  disabled?: boolean
) => {
  return (
    <>
      <Grid item xs={12} md={4}>
        <Controller
          control={control}
          name={`${nation}${type}Entered`}
          as={
            <TextField
              label={`${type} - No Entered For Unit`}
              type="number"
              readOnly={disabled}
              data-test={`${type}-no-entered-for-unit-${nation}`}
            />
          }
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          control={control}
          name={`${nation}${type}DirectlyAffected`}
          as={
            <TextField
              label={`${type} - Directly Affected`}
              type="number"
              readOnly={disabled}
              data-test={`${type}-no-directly-affected-${nation}`}
            />
          }
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          control={control}
          name={`${nation}${type}IndirectlyAffected`}
          as={
            <TextField
              label={`${type} - Indirectly Affected`}
              type="number"
              readOnly={disabled}
              data-test={`${type}-no-indirectly-affected-${nation}`}
            />
          }
        />
      </Grid>
    </>
  );
};
